import React, { useRef, useEffect } from 'react';
import { debounce } from 'utilities';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import InputCurrency from 'components/ui/inputs/InputCurrency';

export const CompensationContainer = styled.div(
  ({ theme }) => `
  label {
    width: 17rem;
    flex-shrink: 0;
    margin-bottom: 0;
    margin-right: 0.5rem;
    position: relative;
    .label-text {
      position: absolute;
      right: 0;
      top: 0;
      bottom: 0;
      margin: auto;
      width: 5.2rem;
      height: 1.3rem;
      font-size: 1rem;
      font-weight: ${theme.fontWeights.fontMedium};
      text-transform: none;
      color: ${theme.darkGray};
      z-index: 1;
    }
    input {
      margin-bottom: 0 !important;
      padding-right: 5.5rem;
    }
    [class^='ErrorMessage'] {
      grid-column: 1 / span 2;
      margin-top: -0.5rem;
    }
  }
`,
);

export const SalaryField = ({ inputProps, ...props }) => {
  const calculatedHourlyContainer = useRef(null);
  const calculatedHourly = useRef(null);
  useEffect(() => {
    calculateHourly(Number(inputProps.value));
  }, [inputProps.value]);

  const calculateHourly = salary => {
    if (salary > 0) {
      const hourly = (salary / 2080).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      });
      calculatedHourly.current.innerText = hourly;
      calculatedHourlyContainer.current.classList.remove('d-none');
    } else {
      calculatedHourly.current.innerText = '';
      calculatedHourlyContainer.current.classList.add('d-none');
    }
  };

  const calculateHourlyDebounce = debounce.call(e => {
    const salary = Number(e.target.value.replace(/,/g, ''));
    calculateHourly(salary);
  });

  return (
    <CompensationContainer>
      <div className="salary-alignments d-block d-md-flex align-items-center mb-4">
        <InputCurrency
          {...inputProps}
          {...props}
          labelText="per year"
          onChange={calculateHourlyDebounce}
          id="minimum_salary_cents"
          data-testid="minimum_salary_cents"
          name="minimum_salary_cents"
          fixedDecimalScale
        />
        <div
          className="my-1 ml-2 calculated-hourly d-none"
          ref={calculatedHourlyContainer}
        >
          equivalent to&nbsp;
          <strong>
            <span ref={calculatedHourly}></span> per&nbsp;hour
          </strong>
        </div>
      </div>
    </CompensationContainer>
  );
};

SalaryField.propTypes = {
  inputProps: PropTypes.any,
  white: PropTypes.string,
};

export const HourlyField = ({ inputProps, ...props }) => {
  const calculatedSalaryContainer = useRef(null);
  const calculatedSalary = useRef(null);

  useEffect(() => {
    calculateSalary(Number(inputProps.value));
  }, [inputProps.value]);

  const calculateSalary = hourly => {
    if (hourly > 0) {
      const salary = (hourly * 2080).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: '0',
        maximumFractionDigits: '2',
      });
      calculatedSalary.current.innerText = salary;
      calculatedSalaryContainer.current.classList.remove('d-none');
    } else {
      calculatedSalary.current.innerText = '';
      calculatedSalaryContainer.current.classList.add('d-none');
    }
  };

  const calculateSalaryDebounce = debounce.call(e => {
    const hourly = Number(e.target.value.replace(/,/g, ''));
    calculateSalary(hourly);
  }, 300);

  return (
    <CompensationContainer>
      <div className="salary-alignments d-block d-md-flex align-items-center mb-4">
        <InputCurrency
          {...inputProps}
          {...props}
          labelText="per hour"
          onChange={calculateSalaryDebounce}
          id="minimum_hourly_rate_cents"
          data-testid="minimum_hourly_rate_cents"
          name="minimum_hourly_rate_cents"
          fixedDecimalScale
        />
        <div
          className="my-1 ml-2 calculated-salary d-none"
          ref={calculatedSalaryContainer}
        >
          equivalent to&nbsp;
          <strong>
            <span ref={calculatedSalary}></span> per&nbsp;year
          </strong>
        </div>
      </div>
    </CompensationContainer>
  );
};

HourlyField.propTypes = {
  inputProps: PropTypes.any,
  white: PropTypes.string,
};
